/**
 * Role vocabulary + capability predicates — the FE's single source of truth for RBAC.
 *
 * The backend is the authorization boundary; these predicates only decide what the UI
 * *offers* (which nav items appear, which enrichment calls fire, which buttons render).
 * Keep them cheap and derivable so route guards, nav, and API clients can share one policy.
 *
 * Contract mirrors `pyxisrc_ai.db.models.Role` on the backend and the RBAC matrix in
 * `pyxis/api/tests/test_rbac_matrix.py`:
 *   - admin / reviewer / sme / viewer → internal PyxisRC staff, cross-client read
 *   - client                          → external, row-scoped to their engagements
 *
 * Import `Role` (the constant object) rather than typing role names as string literals.
 * Renaming a role or adding a new one is then a single edit in this file.
 */

// The wire vocabulary. Everything else in the FE ties back to these names.
export const Role = {
  ADMIN: "admin",
  REVIEWER: "reviewer",
  SME: "sme",
  VIEWER: "viewer",
  CLIENT: "client",
} as const

export type Role = (typeof Role)[keyof typeof Role]

/** Every known role, in the canonical display/order. */
export const ROLES: readonly Role[] = Object.values(Role)

/** Anything the backend considers internal staff. */
export const STAFF_ROLES: readonly Role[] = [Role.ADMIN, Role.REVIEWER, Role.SME, Role.VIEWER]

/** Narrow an untyped role string (from JWT or session storage) into a known Role. */
export function toRole(value: string | null | undefined): Role | null {
  return (ROLES as readonly string[]).includes(value ?? "") ? (value as Role) : null
}

export function isStaff(role: Role | null | undefined): boolean {
  return role != null && STAFF_ROLES.includes(role)
}

export function isClient(role: Role | null | undefined): boolean {
  return role === Role.CLIENT
}

export function isAdmin(role: Role | null | undefined): boolean {
  return role === Role.ADMIN
}

// ── Capability predicates ────────────────────────────────────────────────────
// Prefer these over hand-rolled role checks. They compose the primitives above so
// changing the underlying policy is a one-line edit here, not a codebase-wide grep.

/** Can the caller list every client engagement? Admin-only on the backend. */
export function canListClients(role: Role | null | undefined): boolean {
  return isAdmin(role)
}

/** Can the caller list console users + service keys? Admin-only on the backend. */
export function canManageUsersAndKeys(role: Role | null | undefined): boolean {
  return isAdmin(role)
}

/** Can the caller see reviews across every client's engagements? */
export function canReadAllClients(role: Role | null | undefined): boolean {
  return isStaff(role)
}
